import type { Position } from '@/entities/Position/@x/User';
import {
  fetchAllMembersAsCollaborators,
} from '@/entities/Branch/@x/User';
import api from '@/shared/api';
import type { ApiResponse, UserItem } from '@/shared/lib';
import { useEligibleUsersByPositions } from '@/shared/lib';
import { computed, nextTick, ref, unref, watch, type ComputedRef } from 'vue';
import type { MaybeRef } from 'vue';

export type PositionUserSelectionMode = 'all-users' | 'by-selected-positions';

interface UsePositionUserSelectionOptions {
  mode: PositionUserSelectionMode;
  branchId?: MaybeRef<number | undefined>;
  getUserId?: (user: UserItem) => number | string | undefined;
  fetchMembers?: () => Promise<UserItem[]>;
  extraMembers?: MaybeRef<UserItem[]>;
  /**
   * Two effects when `true`: (1) edit screens reconcile the saved selection against
   * an inactive-inclusive list — without this, an already-selected member who is
   * inactive gets pruned and saving wipes their link; (2) in `by-selected-positions`
   * mode, a second active-only fetch backs the *offered* options, so an inactive
   * member is never presented as a new choice (only kept visible if already recorded).
   * Effect (2) only applies to `by-selected-positions` mode without a custom
   * `fetchMembers` — with a custom `fetchMembers`, or in `all-users` mode, the full
   * list is used and inactive members can be offered as new options again.
   */
  includeInactiveMembers?: boolean;
}

const POSITION_ITEMS_PER_PAGE = 10000;

const haveSameIds = (currentIds: string[], previousIds: string[]) => {
  const currentSet = new Set(currentIds);
  const previousSet = new Set(previousIds);

  return currentSet.size === previousSet.size && [...currentSet].every((id) => previousSet.has(id));
};

const toSortedStringIds = (ids: string[]) =>
  [...ids].sort((a, b) => String(a).localeCompare(String(b)));

const getUserPositionIds = (user: UserItem): number[] => {
  const ids = new Set<number>();

  const singleMembership = user.membership;
  if (singleMembership) {
    if (singleMembership.positionId) ids.add(singleMembership.positionId);
    singleMembership.positions?.forEach((p) => {
      if (p.id) ids.add(p.id);
    });
  }

  user.memberships?.forEach((membership) => {
    if (membership.positionId) {
      ids.add(membership.positionId);
    } else if (membership.positions?.length) {
      membership.positions.forEach((p) => ids.add(p.id));
    } else if (membership.position?.id) {
      ids.add(membership.position.id);
    }
  });

  user.userPositions?.forEach((userPosition) => {
    if (userPosition.positionId) {
      ids.add(userPosition.positionId);
    } else if (userPosition.position?.id) {
      ids.add(userPosition.position.id);
    }
  });

  user.positions?.forEach((position) => {
    if (position.id) {
      ids.add(position.id);
    }
  });

  return Array.from(ids);
};

export function usePositionUserSelection({
  mode,
  branchId: branchIdOption,
  getUserId = (user) => user.memberId ?? user.membership?.id ?? user.userId ?? user.id,
  fetchMembers,
  extraMembers,
  includeInactiveMembers = false,
}: UsePositionUserSelectionOptions) {
  let isFetching = false;
  let lastFetchedPositionIds: string[] = [];
  const allMembers = ref<UserItem[]>([]);
  // Active-only roster, used to build newly OFFERED options when includeInactiveMembers is true.
  // allMembers (which may include inactive members) stays the source of truth for resolving an
  // already-recorded/selected member's name, mirroring EditHomologationModal's withRecordedPosition.
  const activeMembers = ref<UserItem[]>([]);

  const withExtraMembers = (members: UserItem[]): UserItem[] => {
    const extras = unref(extraMembers) ?? [];
    if (extras.length === 0) return members;
    const existingIds = new Set(members.map((member) => getUserId(member)));
    const additional = extras.filter((member) => !existingIds.has(getUserId(member)));
    return additional.length > 0 ? [...members, ...additional] : members;
  };

  const positions = ref<ApiResponse<Position[]>>({
    success: false,
    data: [],
  });
  const users = ref<ApiResponse<UserItem[]>>({
    success: false,
    data: [],
  });
  const usersLoaded = ref(false);
  const selectedPositions = ref<string[]>([]);
  const selectedUsers = ref<string[]>([]);

  const { eligibleUsers, reconcileSelectedUsers } = useEligibleUsersByPositions({
    users: computed(() => users.value.data ?? []),
    selectedPositions,
    selectedUsers,
    getUserId,
    getUserPositionIds,
    canReconcile: usersLoaded,
  });

  const mergeRecordedInactiveSelections = (
    activeMatched: UserItem[],
    matchesSelectedPosition: (member: UserItem) => boolean
  ): UserItem[] => {
    if (!includeInactiveMembers) {
      return activeMatched;
    }

    const offeredIds = new Set(activeMatched.map((member) => String(getUserId(member) ?? '')));
    const recordedSelections = allMembers.value.filter((member) => {
      const id = String(getUserId(member) ?? '');
      return (
        id !== '' &&
        selectedUsers.value.includes(id) &&
        !offeredIds.has(id) &&
        matchesSelectedPosition(member)
      );
    });

    return recordedSelections.length > 0 ? [...activeMatched, ...recordedSelections] : activeMatched;
  };

  const fetchUsersBySelectedPositions = async (positionIds: string[]) => {
    if (mode !== 'by-selected-positions') {
      return;
    }

    if (isFetching) {
      return;
    }

    const sortedPositionIds = toSortedStringIds(positionIds);
    const sortedLastFetched = toSortedStringIds(lastFetchedPositionIds);

    if (
      sortedPositionIds.length === sortedLastFetched.length &&
      sortedPositionIds.every((id, index) => id === sortedLastFetched[index])
    ) {
      return;
    }

    isFetching = true;
    usersLoaded.value = false;

    try {
      if (positionIds.length === 0) {
        users.value = {
          success: true,
          data: [],
        };
        selectedUsers.value = [];
        lastFetchedPositionIds = [];
        usersLoaded.value = true;
        isFetching = false;
        return;
      }

      const selectedPositionIds = new Set(
        positionIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      );
      const matchesSelectedPosition = (member: UserItem) =>
        getUserPositionIds(member).some((positionId) => selectedPositionIds.has(positionId));

      const activeMatched = (includeInactiveMembers ? activeMembers.value : allMembers.value).filter(
        matchesSelectedPosition
      );

      users.value = {
        success: true,
        data: mergeRecordedInactiveSelections(activeMatched, matchesSelectedPosition),
      };

      reconcileSelectedUsers();
      lastFetchedPositionIds = [...positionIds];
    } catch {
      users.value = {
        success: false,
        message: 'Failed to fetch users for selected positions',
        data: [],
      };
    } finally {
      usersLoaded.value = true;
      isFetching = false;
    }
  };

  const fetchPositionsAndUsers = async () => {
    usersLoaded.value = false;

    try {
      const resolvedBranchId = unref(branchIdOption);
      const positionsParams: { itemsPerPage: number; branchId?: number } = {
        itemsPerPage: POSITION_ITEMS_PER_PAGE,
      };
      if (resolvedBranchId !== undefined && Number.isFinite(resolvedBranchId) && resolvedBranchId > 0) {
        positionsParams.branchId = resolvedBranchId;
      }

      const positionsPromise = api.get('/positions', {
        params: positionsParams,
      });
      const membersFilter = includeInactiveMembers ? { includeInactiveMembers: true } : undefined;
      const membersPromise = fetchMembers
        ? fetchMembers()
        : fetchAllMembersAsCollaborators(resolvedBranchId, membersFilter);

      if (mode === 'all-users') {
        const [positionsResponse, memberUsers] = await Promise.all([
          positionsPromise,
          membersPromise,
        ]);

        positions.value = {
          success: true,
          data: positionsResponse.data?.data || [],
        };
        allMembers.value = withExtraMembers(memberUsers);
        users.value = {
          success: true,
          data: allMembers.value,
        };
        return;
      }

      const activeMembersPromise =
        includeInactiveMembers && !fetchMembers
          ? fetchAllMembersAsCollaborators(resolvedBranchId, undefined)
          : undefined;

      const [positionsResponse, memberUsers, activeMemberUsers] = await Promise.all([
        positionsPromise,
        membersPromise,
        activeMembersPromise ?? Promise.resolve<UserItem[] | undefined>(undefined),
      ]);

      positions.value = {
        success: true,
        data: positionsResponse.data?.data || [],
      };
      allMembers.value = withExtraMembers(memberUsers);
      activeMembers.value = withExtraMembers(activeMemberUsers ?? memberUsers);

      if (selectedPositions.value.length > 0) {
        await fetchUsersBySelectedPositions(selectedPositions.value);
      } else {
        users.value = {
          success: true,
          data: [],
        };
      }
    } catch {
      positions.value = {
        success: false,
        message: 'Failed to fetch positions',
      };
      users.value = {
        success: false,
        message: 'Failed to fetch users',
        data: [],
      };
    } finally {
      usersLoaded.value = true;
    }
  };

  if (mode === 'by-selected-positions') {
    watch(
      selectedPositions,
      async (newPositions, oldPositions) => {
        await nextTick();

        if (haveSameIds(newPositions, oldPositions ?? [])) {
          return;
        }

        await fetchUsersBySelectedPositions(newPositions);
      },
      { immediate: false }
    );
  }

  const resetSelection = () => {
    selectedPositions.value = [];
    selectedUsers.value = [];

    if (mode === 'by-selected-positions') {
      isFetching = false;
      lastFetchedPositionIds = [];
      usersLoaded.value = false;
      allMembers.value = [];
      activeMembers.value = [];
    }
  };

  type InitializeSelectedUserIds = string[] | ((eligibleUsers: UserItem[]) => string[]);

  const initializeSelections = async (positionIds: string[], userIds: InitializeSelectedUserIds) => {
    if (positionIds.length === 0) return;
    selectedPositions.value = [...positionIds];
    await fetchUsersBySelectedPositions(positionIds);
    const eligibleUsers = users.value.data ?? [];
    const resolved = typeof userIds === 'function' ? userIds(eligibleUsers) : userIds;
    selectedUsers.value = [...resolved];

    if (includeInactiveMembers) {
      const selectedPositionIds = new Set(
        positionIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      );
      const matchesSelectedPosition = (member: UserItem) =>
        getUserPositionIds(member).some((positionId) => selectedPositionIds.has(positionId));

      users.value = {
        ...users.value,
        data: mergeRecordedInactiveSelections(users.value.data ?? [], matchesSelectedPosition),
      };
    }
  };

  const filteredUsers: ComputedRef<ApiResponse<UserItem[]>> = computed(() => {
    if (mode !== 'all-users') return users.value;
    return { ...users.value, data: eligibleUsers.value };
  });

  const positionsSelectedCount = computed(() => selectedPositions.value.length);
  const usersSelectedCount = computed(() => selectedUsers.value.length);

  return {
    positions,
    users,
    filteredUsers,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    fetchPositionsAndUsers,
    resetSelection,
    initializeSelections,
  };
}
